import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SoundManager from '../components/SoundManager';

const BOARD_SIZE = 40;
const PLAYER = 'player';
const AI = 'ai';

// Board operations for each position (update to match the image)
const boardOperations = {
  2: { op: '+', value: 1 },
  5: { op: '-', value: 1 },
  6: { op: '+', value: 2 },
  10: { op: '-', value: 2 },
  12: { op: '+', value: 3 },
  13: { op: '-', value: 2 },
  17: { op: '+', value: 4 },
  20: { op: '-', value: 2 },
  23: { op: '+', value: 2 },
  26: { op: '-', value: 4 },
  28: { op: '+', value: 3 },
  30: { op: '-', value: 1 },
  32: { op: '-', value: 5 },
  35: { op: '+', value: 3 },
  37: { op: '+', value: 1 },
  39: { op: '-', value: 1 },
};

// Helper to get all board positions with equations
const equationPositions = Object.keys(boardOperations).map(Number);

function getNextPosition(pos, op) {
  if (!op) return pos;
  switch (op.op) {
    case '+': return pos + op.value;
    case '-': return pos - op.value;
    case 'x': return pos * op.value;
    case '÷': return Math.floor(pos / op.value);
    default: return pos;
  }
}

function getBoardRows() {
  // Returns the board layout as rows for rendering
  return [
    [1,2,3,4,5],
    [13,12,11,10,9,8,7,6],
    [14,15,16,17,18,19,20,21],
    [29,28,27,26,25,24,23,22],
    [30,31,32,33,34,35,36,37],
    [40,39,38]
  ];
}

function randomDice() {
  return Math.floor(Math.random() * 6) + 1;
}

function randomDiceForPlayer(currentPos) {
  // 60% chance to land on a board number with an equation
  if (Math.random() < 0.6) {
    // Find all possible dice rolls (2-12) that would land on an equation
    const possibleMoves = equationPositions
      .map(eqPos => eqPos - currentPos)
      .filter(move => move >= 2 && move <= 12);
    if (possibleMoves.length > 0) {
      const move = possibleMoves[Math.floor(Math.random() * possibleMoves.length)];
      // Find a dice pair that sums to move
      for (let d1 = 1; d1 <= 6; d1++) {
        for (let d2 = 1; d2 <= 6; d2++) {
          if (d1 + d2 === move) {
            return [d1, d2];
          }
        }
      }
    }
  }
  // Otherwise, random dice
  return [randomDice(), randomDice()];
}

function DamatLevel1Game({ soundContext }) {
  const [playerPos, setPlayerPos] = useState(1);
  const [aiPos, setAiPos] = useState(1);
  const [turn, setTurn] = useState(PLAYER);
  const [dice, setDice] = useState([null, null]);
  const [rolling, setRolling] = useState(false);
  const [operation, setOperation] = useState(null);
  const [showAnswers, setShowAnswers] = useState(false);
  const [answers, setAnswers] = useState([]);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [feedback, setFeedback] = useState('Welcome! Roll the dice to move your train! 🚂');
  const [gameEnded, setGameEnded] = useState(false);
  const [winner, setWinner] = useState(null);
  const [motivation, setMotivation] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);

  // Motivational messages
  const correctMessages = [
    'Great job!',
    'Awesome! 🎉',
    'You did it!',
    'Keep it up!',
    'Correct! 🚂',
    'Fantastic!',
    'Brilliant!',
    'Superb!',
    'You rock!',
    'Math Master!'
  ];
  const wrongMessages = [
    "Don't give up!",
    'Try again next time!',
    'Keep practicing!',
    'Almost there!',
    'Stay positive!',
    'You can do it!',
    'Mistakes help us learn!',
    'Keep going!',
    'Believe in yourself!',
    'Every try counts!'
  ];

  // Automatically roll dice for AI turn
  useEffect(() => {
    if (turn === AI && !gameEnded && !showAnswers && !rolling) {
      // Add a slight delay for realism
      const timeout = setTimeout(() => {
        handleRollDice();
      }, 1200);
      return () => clearTimeout(timeout);
    }
  }, [turn, gameEnded, showAnswers, rolling]);

  // Show motivational popup for 1.5s
  useEffect(() => {
    if (motivation) {
      const timeout = setTimeout(() => setMotivation(null), 1500);
      return () => clearTimeout(timeout);
    }
  }, [motivation]);

  // Roll dice and move
  const handleRollDice = () => {
    if (rolling || gameEnded) return;
    
    // Play dice sound
    soundContext?.playDiceSound();
    
    setRolling(true);
    setTimeout(() => {
      let d1, d2;
      if (turn === PLAYER) {
        [d1, d2] = randomDiceForPlayer(playerPos);
      } else {
        d1 = randomDice();
        d2 = randomDice();
      }
      setDice([d1, d2]);
      setRolling(false);
      const move = d1 + d2;
      if (gameEnded) return; // Prevent move if game ended during delay
      if (turn === PLAYER) {
        movePlayer(playerPos, move);
      } else {
        setTimeout(() => {
          if (!gameEnded) moveAI(aiPos, move);
        }, 1000);
      }
    }, 600);
  };

  // Move player and handle operation
  function movePlayer(start, move) {
    let newPos = Math.min(start + move, BOARD_SIZE);
    setPlayerPos(newPos);
    if (newPos === BOARD_SIZE) {
      setGameEnded(true);
      setWinner(PLAYER);
      setFeedback('🎉 You win!');
      setShowAnswers(false);
      // Play win sound
      soundContext?.playGameWinSound();
      return;
    }
    handleOperation(newPos, PLAYER);
  }

  // Move AI and handle operation
  function moveAI(start, move) {
    let newPos = Math.min(start + move, BOARD_SIZE);
    setAiPos(newPos);
    if (newPos === BOARD_SIZE) {
      setGameEnded(true);
      setWinner(AI);
      setFeedback('🤖 AI wins!');
      setShowAnswers(false);
      // Play win sound
      soundContext?.playGameWinSound();
      return;
    }
    handleOperation(newPos, AI);
  }

  // Handle operation on landing
  function handleOperation(pos, who) {
    if (gameEnded) return;
    const op = boardOperations[pos];
    if (op) {
      // Play notification sound for equation
      soundContext?.playNotificationSound();
      
      // Prepare answers
      const correct = getNextPosition(pos, op);
      const wrong1 = correct + (Math.random() > 0.5 ? 1 : -1);
      const wrong2 = correct + (Math.random() > 0.5 ? 2 : -2);
      const wrong3 = correct + (Math.random() > 0.5 ? 3 : -3);
      
      const allAnswers = [correct, wrong1, wrong2, wrong3];
      const shuffled = allAnswers.sort(() => Math.random() - 0.5);
      
      setOperation(op);
      setCorrectAnswer(correct);
      setAnswers(shuffled);
      setShowAnswers(true);
      
      if (who === PLAYER) {
        setFeedback(`Solve: ${pos} ${op.op} ${op.value} = ?`);
      } else {
        // AI solves automatically
        setTimeout(() => {
          if (!gameEnded) aiSolve(pos, op, correct, shuffled);
        }, 1500);
      }
    } else {
      // No operation, just change turn
      nextTurn();
    }
  }

  // Handle answer selection
  function selectAnswer(ans) {
    if (showAnswers && turn === PLAYER) {
      setShowAnswers(false);
      if (ans === correctAnswer) {
        setMotivation(correctMessages[Math.floor(Math.random() * correctMessages.length)]);
        setPlayerPos(correctAnswer);
        // Play correct answer sound
        soundContext?.playCorrectAnswerSound();
      } else {
        setMotivation(wrongMessages[Math.floor(Math.random() * wrongMessages.length)]);
        // Play wrong answer sound
        soundContext?.playWrongAnswerSound();
      }
      setTimeout(() => {
        if (!gameEnded) nextTurn();
      }, 1000);
    }
  }

  // AI solves the equation
  function aiSolve(pos, op, correct, shuffled) {
    if (gameEnded) return;
    
    // AI has 80% chance to get it right
    const aiCorrect = Math.random() < 0.8;
    const aiAnswer = aiCorrect ? correct : shuffled.find(a => a !== correct);
    
    setShowAnswers(false);
    setAiPos(aiAnswer);
    
    if (aiCorrect) {
      // Play correct answer sound for AI
      soundContext?.playCorrectAnswerSound();
    } else {
      // Play wrong answer sound for AI
      soundContext?.playWrongAnswerSound();
    }
    
    setTimeout(() => {
      if (!gameEnded) nextTurn();
    }, 1000);
  }

  // Change turn
  function nextTurn() {
    // Play turn change sound
    soundContext?.playTurnChangeSound();
    
    setTurn(turn === PLAYER ? AI : PLAYER);
    setDice([null, null]);
    setOperation(null);
    setShowAnswers(false);
    setAnswers([]);
    setCorrectAnswer(null);
    
    if (turn === PLAYER) {
      setFeedback("Your turn! Roll the dice! 🎲");
    } else {
      setFeedback("AI is thinking... ");
    }
  }

  // Restart game
  function restartGame() {
    setPlayerPos(1);
    setAiPos(1);
    setTurn(PLAYER);
    setDice([null, null]);
    setRolling(false);
    setOperation(null);
    setShowAnswers(false);
    setAnswers([]);
    setCorrectAnswer(null);
    setFeedback('Welcome! Roll the dice to move your train! 🚂');
    setGameEnded(false);
    setWinner(null);
  }

  // Resume audio context on first interaction
  const handleUserInteraction = () => {
    if (!hasInteracted && soundContext && soundContext.resumeAudio) {
      soundContext.resumeAudio();
      setHasInteracted(true);
    }
  };

  // Render board
  const boardRows = getBoardRows();
  function renderTrainCar(pos) {
    // Determine operation
    const op = boardOperations[pos];
    // Car color and style
    const isStart = pos === 1;
    const isFinish = pos === BOARD_SIZE;
    const isPlayer = playerPos === pos;
    const isAI = aiPos === pos;
    // Car base style
    const carStyle = {
      position: 'relative',
      width: 70,
      height: 70,
      margin: 4,
      borderRadius: 16,
      background: isStart
        ? 'linear-gradient(45deg, #FFD700, #FFA500)'
        : isFinish
        ? 'linear-gradient(45deg, #FF1493, #FF69B4)'
        : 'linear-gradient(135deg, #ff6b6b 60%, #ffb347 100%)',
      border: isStart
        ? '3px solid #FF8C00'
        : isFinish
        ? '3px solid #DC143C'
        : '3px solid #d63447',
      boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      fontWeight: 'bold',
      color: isStart ? '#333' : '#fff',
      fontSize: 22,
      overflow: 'visible',
      zIndex: isPlayer || isAI ? 2 : 1,
      borderBottom: '8px solid #b5651d', // wheels effect
      borderTop: '8px solid #ffe066', // roof effect
      transition: 'transform 0.2s',
      transform: isPlayer || isAI ? 'scale(1.08)' : 'none',
    };
    // Stripes
    const stripes = (
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: 16,
        display: 'flex',
        zIndex: 0,
      }}>
        {[...Array(5)].map((_, i) => (
          <div key={i} style={{
            flex: 1,
            background: i % 2 === 0 ? '#fff' : '#ffb347',
            opacity: 0.7,
          }} />
        ))}
      </div>
    );
    // Wheels
    const wheels = (
      <div style={{
        position: 'absolute',
        bottom: -10,
        left: 10,
        width: 50,
        display: 'flex',
        justifyContent: 'space-between',
        zIndex: 2,
      }}>
        <div style={{ width: 12, height: 12, background: '#3498db', borderRadius: '50%', border: '2px solid #333' }} />
        <div style={{ width: 12, height: 12, background: '#3498db', borderRadius: '50%', border: '2px solid #333' }} />
      </div>
    );
    // Operation bubble
    const opBubble = op && (
      <div style={{
        position: 'absolute',
        top: -12,
        right: -12,
        background: op.op === '+' ? '#4ecdc4' : '#ff6b6b',
        color: '#fff',
        borderRadius: '50%',
        width: 32,
        height: 32,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 'bold',
        fontSize: 18,
        border: '3px solid #fff',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 3,
      }}>
        {op.op}{op.value}
      </div>
    );
    // Player/AI/Start/Finish icons
    const icon = isStart
      ? <span style={{ position: 'absolute', top: -30, left: -18, fontSize: 32 }}>🚂</span>
      : isFinish
      ? <span style={{ position: 'absolute', top: -30, left: 18, fontSize: 32 }}>🐵</span>
      : null;
    const playerToken = isPlayer && (
      <span style={{ position: 'absolute', top: -18, left: 10, fontSize: 28, zIndex: 4 }}>👨‍🎓</span>
    );
    const aiToken = isAI && (
      <span style={{ position: 'absolute', top: -18, right: 10, fontSize: 28, zIndex: 4 }}>🤖</span>
    );
    return (
      <div key={pos} style={carStyle}>
        {stripes}
        {wheels}
        {opBubble}
        {icon}
        {playerToken}
        {aiToken}
        <div style={{ position: 'relative', zIndex: 2 }}>{pos}</div>
      </div>
    );
  }

  return (
    <div className="game-container" style={{ maxWidth: 1400, margin: '0 auto', background: 'white', borderRadius: 20, boxShadow: '0 10px 30px rgba(0,0,0,0.2)', overflow: 'hidden', position: 'relative', zIndex: 0 }} onClick={handleUserInteraction}>
      <div className="header" style={{ background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)', color: 'white', padding: 20, textAlign: 'center', position: 'relative' }}>
        <h1 style={{ fontSize: '2.5em', marginBottom: 5, textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>🚂 DAMAT 🚂</h1>
        <div className="subtitle" style={{ fontSize: '1.2em', opacity: 0.9 }}>Mathematical Train Adventure</div>
        <div className="level-badge" style={{ background: '#FFD700', color: '#333', padding: '8px 20px', borderRadius: 20, display: 'inline-block', marginTop: 10, fontWeight: 'bold', boxShadow: '0 3px 10px rgba(0,0,0,0.2)' }}>
          LEVEL 1 - Addition & Subtraction
        </div>
      </div>
      <div className="main-content" style={{ display: 'grid', gridTemplateColumns: '1fr 350px', gap: 20, padding: 20 }}>
        <div className="board-container" style={{ background: 'linear-gradient(135deg, #87CEEB 0%, #B0E0E6 100%)', borderRadius: 15, padding: 20, boxShadow: '0 5px 15px rgba(0,0,0,0.1)', position: 'relative', overflow: 'hidden' }}>
          <div className="board-title" style={{ textAlign: 'center', color: '#2c3e50', fontSize: '1.8em', fontWeight: 'bold', marginBottom: 20, textShadow: '2px 2px 4px rgba(255,255,255,0.5)' }}>
            🌟 Train Station Journey 🌟
          </div>
          <div className="train-board">
            {boardRows.map((row, i) => (
              <div key={i} className={`train-row${i % 2 === 1 ? ' reverse' : ''}`} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: 15, gap: 8, flexDirection: i % 2 === 1 ? 'row-reverse' : 'row' }}>
                {row.map(renderTrainCar)}
              </div>
            ))}
          </div>
          {/* New Game Button */}
          <div style={{ textAlign: 'center', marginTop: 32 }}>
            <button
              onClick={restartGame}
              style={{
                padding: '16px 40px',
                background: 'linear-gradient(90deg, #ff6b6b, #4ecdc4)',
                color: '#fff',
                border: 'none',
                borderRadius: 16,
                fontSize: '1.3em',
                fontWeight: 'bold',
                boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                cursor: 'pointer',
                marginTop: 8,
                marginBottom: 8,
                transition: 'background 0.2s',
              }}
            >
              🔄 New Game
            </button>
          </div>
          {/* Motivation Popup (below the button) */}
          {motivation && (
            <div style={{
              margin: '24px auto 0 auto',
              maxWidth: 600,
              background: 'linear-gradient(90deg, #4ecdc4, #ffb347)',
              color: '#fff',
              padding: '20px 36px',
              borderRadius: 20,
              fontSize: '1.7em',
              fontWeight: 'bold',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              textAlign: 'center',
              animation: 'pop 0.3s',
            }}>
              {motivation}
            </div>
          )}
          {/* Winner Announcement (below the button) */}
          {gameEnded && (
            <div style={{
              margin: '32px auto 0 auto',
              maxWidth: 600,
              background: winner === PLAYER ? 'linear-gradient(90deg, #4ecdc4, #ffb347)' : 'linear-gradient(90deg, #6f42c1, #8e5ed9)',
              color: '#fff',
              padding: '24px 48px',
              borderRadius: 20,
              fontSize: '2.2em',
              fontWeight: 'bold',
              boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
              textAlign: 'center',
              animation: 'pop 0.3s',
            }}>
              {winner === PLAYER ? '🎉 You Win! Congratulations!' : '🤖 AI Buddy Wins! Try Again!'}
            </div>
          )}
        </div>
        <div className="game-panel" style={{ background: '#f8f9fa', borderRadius: 15, padding: 20, boxShadow: '0 5px 15px rgba(0,0,0,0.1)', height: 'fit-content' }}>
          <div className={`current-turn ${turn === PLAYER ? 'player-turn' : 'ai-turn'}`} style={{ textAlign: 'center', marginBottom: 20, padding: 15, borderRadius: 10, fontSize: '1.2em', fontWeight: 'bold', background: turn === PLAYER ? 'linear-gradient(135deg, #28a745, #20c997)' : 'linear-gradient(135deg, #6f42c1, #8e5ed9)', color: 'white' }}>
            {turn === PLAYER ? '🎯 Your Turn!' : '🤖 AI Buddy Turn'}
          </div>
          <div className="player-info" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 15, marginBottom: 20 }}>
            <div className={`player-card${turn === PLAYER ? ' active' : ''}`} style={{ background: 'white', padding: 15, borderRadius: 10, textAlign: 'center', boxShadow: '0 3px 10px rgba(0,0,0,0.1)', border: turn === PLAYER ? '3px solid #28a745' : 'none' }}>
              <div className="player-icon" style={{ fontSize: '2em', marginBottom: 5 }}>👨‍🎓</div>
              <div>You</div>
              <div className="player-position">Position: <span>{playerPos}</span></div>
            </div>
            <div className={`player-card ai${turn === AI ? ' active' : ''}`} style={{ background: 'white', padding: 15, borderRadius: 10, textAlign: 'center', boxShadow: '0 3px 10px rgba(0,0,0,0.1)', border: turn === AI ? '3px solid #6f42c1' : 'none' }}>
              <div className="player-icon" style={{ fontSize: '2em', marginBottom: 5 }}>🤖</div>
              <div>AI Buddy</div>
              <div className="player-position">Position: <span>{aiPos}</span></div>
            </div>
          </div>
          <div className="dice-section" style={{ marginBottom: 25 }}>
            <h3 style={{ textAlign: 'center', marginBottom: 15, color: '#2c3e50' }}>🎲 Roll the Dice!</h3>
            <div className="dice-container" style={{ display: 'flex', justifyContent: 'center', gap: 15, margin: '15px 0' }}>
              <div className={`dice${rolling ? ' rolling' : ''}`} style={{ width: 70, height: 70, background: 'white', border: '4px solid #333', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 'bold', boxShadow: '0 6px 12px rgba(0,0,0,0.2)', transition: 'all 0.3s ease' }}>{dice[0] || '?'}</div>
              <div className={`dice${rolling ? ' rolling' : ''}`} style={{ width: 70, height: 70, background: 'white', border: '4px solid #333', borderRadius: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 28, fontWeight: 'bold', boxShadow: '0 6px 12px rgba(0,0,0,0.2)', transition: 'all 0.3s ease' }}>{dice[1] || '?'}</div>
            </div>
            <div className="operation-display" style={{ textAlign: 'center', fontSize: '1.5em', fontWeight: 'bold', color: '#2c3e50', margin: '15px 0', padding: 10, background: '#e9ecef', borderRadius: 8, minHeight: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {operation ? `${turn === PLAYER ? playerPos : aiPos} ${operation.op}${operation.value}` : (dice[0] && dice[1] ? `Move ${dice[0] + dice[1]} spaces!` : 'Roll dice to start!')}
            </div>
            {turn === PLAYER && !showAnswers && !rolling && !gameEnded && (
              <button className="roll-dice-btn" style={{ width: '100%', padding: 15, background: 'linear-gradient(135deg, #ff6b6b, #4ecdc4)', color: 'white', border: 'none', borderRadius: 12, fontSize: '1.2em', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s ease', marginBottom: 20 }} onClick={handleRollDice} disabled={rolling || showAnswers}>
                🎲 Roll Dice
              </button>
            )}
            {showAnswers && (
              <div id="answerSection" style={{ display: 'block' }}>
                <div className="answer-buttons" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 15 }}>
                  {answers.map((ans, i) => (
                    <button key={i} className="answer-btn" style={{ padding: 12, background: 'linear-gradient(135deg, #17a2b8, #138496)', color: 'white', border: 'none', borderRadius: 8, fontSize: '1.1em', fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.3s ease' }} onClick={() => selectAnswer(ans)} disabled={rolling}>{ans}</button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className={`feedback-message feedback-info`} style={{ textAlign: 'center', padding: 15, borderRadius: 10, marginBottom: 20, fontWeight: 'bold', background: '#d1ecf1', color: '#0c5460', border: '2px solid #bee5eb', transition: 'all 0.3s ease' }}>
            {feedback}
          </div>
          <div className="game-rules" style={{ background: '#fff3cd', border: '2px solid #ffeaa7', borderRadius: 10, padding: 15, marginTop: 20 }}>
            <h4 style={{ color: '#856404', marginBottom: 10 }}>📖 How to Play:</h4>
            <ul style={{ color: '#856404', paddingLeft: 20 }}>
              <li>Roll dice and move by the total</li>
              <li>If you land on a space with math (+/-), solve it!</li>
              <li>Move to the answer position</li>
              <li>Keep solving if new space has math too!</li>
              <li>First to reach station 40 wins! 🏆</li>
            </ul>
          </div>
          {gameEnded && (
            <div className="winner-modal" style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
              <div className="winner-content" style={{ background: 'white', padding: 40, borderRadius: 20, textAlign: 'center', boxShadow: '0 20px 40px rgba(0,0,0,0.3)', animation: 'celebrate 0.5s ease-in-out' }}>
                <h2 style={{ fontSize: '3em', marginBottom: 20, color: '#ff6b6b' }}>{winner === PLAYER ? '🎉 You Win!' : '🤖 AI Wins!'}</h2>
                <button className="restart-btn" style={{ padding: '15px 30px', background: 'linear-gradient(135deg, #28a745, #20c997)', color: 'white', border: 'none', borderRadius: 12, fontSize: '1.2em', fontWeight: 'bold', cursor: 'pointer', marginTop: 20 }} onClick={restartGame}>Restart Game</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const TryDemo = () => {
  const [gameStarted, setGameStarted] = useState(false);

  return (
    <SoundManager>
      {(soundContext) => (
        <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
          {/* Header Section */}
          <section className="pt-32 pb-20">
            <div className="max-w-7xl mx-auto px-4">
              <div className="text-center mb-16">
                <h1 className="text-5xl font-bold text-gray-800 mb-6">🎮 Try DAMAT Now!</h1>
                <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
                  Play a quick round of DAMAT and see how fun math can be! Experience the excitement of 
                  learning arithmetic through gameplay.
                </p>
              </div>
            </div>
          </section>

          {/* Game Instructions */}
          <section className="pb-10">
            <div className="max-w-7xl mx-auto px-4">
              <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
                <h2 className="text-3xl font-bold text-center text-gray-800 mb-8">📋 How to Play</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div className="text-center p-6 bg-gradient-to-br from-red-50 to-red-100 rounded-xl">
                    <div className="bg-red-500 text-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">1</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">🎲 Click to roll the die</h3>
                    <p className="text-gray-600 text-sm">Start your turn by rolling to get your numbers</p>
                  </div>
                  
                  <div className="text-center p-6 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-xl">
                    <div className="bg-yellow-500 text-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">2</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">➕ Solve the equation</h3>
                    <p className="text-gray-600 text-sm">Use mental math to calculate the result</p>
                  </div>
                  
                  <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                    <div className="bg-green-500 text-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">3</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">🚀 Move your token forward</h3>
                    <p className="text-gray-600 text-sm">Advance on the board based on your answer</p>
                  </div>
                  
                  <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                    <div className="bg-blue-500 text-white p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                      <span className="text-2xl font-bold">4</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2">🎯 Reach the final square!</h3>
                    <p className="text-gray-600 text-sm">First to finish wins the game</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* DEMO GAME SECTION */}
          <DamatLevel1Game soundContext={soundContext} />

          {/* Game Features */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-16">🌟 What Makes DAMAT Special?</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                <div className="text-center p-8 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl">
                  <div className="text-5xl mb-4">🧠</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Mental Math Mastery</h3>
                  <p className="text-gray-600">
                    Builds quick calculation skills through repeated practice and pattern recognition
                  </p>
                </div>
                
                <div className="text-center p-8 bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl">
                  <div className="text-5xl mb-4">🎯</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Curriculum Aligned</h3>
                  <p className="text-gray-600">
                    Supports Malaysian KSSR mathematics objectives for primary school students
                  </p>
                </div>
                
                <div className="text-center p-8 bg-gradient-to-br from-green-50 to-green-100 rounded-2xl">
                  <div className="text-5xl mb-4">👥</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Social Learning</h3>
                  <p className="text-gray-600">
                    Encourages peer interaction and collaborative problem-solving skills
                  </p>
                </div>
                
                <div className="text-center p-8 bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-2xl">
                  <div className="text-5xl mb-4">🎲</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Game-Based Learning</h3>
                  <p className="text-gray-600">
                    Makes mathematics enjoyable through strategic gameplay and competition
                  </p>
                </div>
                
                <div className="text-center p-8 bg-gradient-to-br from-red-50 to-red-100 rounded-2xl">
                  <div className="text-5xl mb-4">📚</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Multiple Operations</h3>
                  <p className="text-gray-600">
                    Covers addition, subtraction, multiplication, and division in progressive levels
                  </p>
                </div>
                
                <div className="text-center p-8 bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl">
                  <div className="text-5xl mb-4">🏆</div>
                  <h3 className="text-xl font-bold text-gray-800 mb-4">Achievement System</h3>
                  <p className="text-gray-600">
                    Motivates students with clear goals and milestone celebrations
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Success Message */}
          <section className="py-20 bg-gradient-to-r from-green-400 to-blue-500 text-white">
            <div className="max-w-7xl mx-auto px-4 text-center">
              <div className="text-6xl mb-6">🎉</div>
              <h2 className="text-4xl font-bold mb-6">You did it!</h2>
              <p className="text-xl mb-8 max-w-2xl mx-auto">
                Great job trying out the DAMAT demo! Now you've experienced how fun and engaging math learning can be. 
                Ready to bring this excitement to your classroom or home?
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/products"
                  className="bg-white text-green-600 px-8 py-4 rounded-xl font-semibold text-lg shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 inline-flex items-center justify-center"
                >
                  🛍️ Explore DAMAT Sets →
                </Link>
                <Link
                  to="/contact"
                  className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-xl font-semibold text-lg hover:bg-white hover:text-green-600 transition-all duration-200 inline-flex items-center justify-center"
                >
                  📦 Pre-Order Now
                </Link>
              </div>
            </div>
          </section>

          {/* Educational Benefits */}
          <section className="py-20 bg-white">
            <div className="max-w-7xl mx-auto px-4">
              <h2 className="text-4xl font-bold text-center text-gray-800 mb-16">📖 Educational Research Behind DAMAT</h2>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                <div>
                  <img
                    src="https://images.pexels.com/photos/374918/pexels-photo-374918.jpeg"
                    alt="Math equation on blackboard"
                    className="rounded-2xl shadow-lg"
                  />
                </div>
                
                <div className="space-y-6">
                  <div className="bg-blue-50 p-6 rounded-2xl">
                    <h3 className="text-2xl font-bold text-blue-600 mb-3">🔬 Research-Based Design</h3>
                    <p className="text-gray-600">
                      Developed by education researchers at Universiti Malaysia Perlis (UniMAP) based on 
                      proven pedagogical principles and local curriculum requirements.
                    </p>
                  </div>
                  
                  <div className="bg-green-50 p-6 rounded-2xl">
                    <h3 className="text-2xl font-bold text-green-600 mb-3">📊 Proven Results</h3>
                    <p className="text-gray-600">
                      Studies show that students using DAMAT improve their mental math speed by 45% 
                      and show increased enthusiasm for mathematics learning.
                    </p>
                  </div>
                  
                  <div className="bg-purple-50 p-6 rounded-2xl">
                    <h3 className="text-2xl font-bold text-purple-600 mb-3">🎯 Curriculum Integration</h3>
                    <p className="text-gray-600">
                      Seamlessly aligns with KSSR mathematics standards, making it easy for teachers 
                      to incorporate into existing lesson plans and assessment strategies.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      )}
    </SoundManager>
  );
};

export default TryDemo;